import {
  Checkbox,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ResponsiveTable } from "@floriday/floriday-ui";
import {
  useProductRegulatoryFeatures,
  useSetPrimaryCharacteristic,
} from "../../hooks/useProductRegulatoryFeatures";
import { useProductDetails } from "../../hooks/useProductDetails";
import { formatDate, formatDateTime } from "../../utils/dateFormat";
import { useFeatureFlag, Feature } from "../../hooks/useFeatureFlag";

interface Props {
  productId: string;
}

export function RegulatoryFeaturesTab({ productId }: Props) {
  const { t } = useTranslation();

  const { data: productDetails } = useProductDetails(productId);
  const { data: regulatoryFeatures, isLoading } =
    useProductRegulatoryFeatures(productId);

  const { mutate: setPrimary, isPending } =
    useSetPrimaryCharacteristic(productId);

  const canSetPrimary = useFeatureFlag(Feature.SetPrimaryCharacteristic);

  const handleCheckboxChange = (characteristicCode: string) => {
    if (!productDetails?.code) {
      console.error("Product code not available");
      return;
    }
    setPrimary({ productCode: productDetails.code, characteristicCode });
  };

  if (isLoading) {
    return null;
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ p: 0 }}>
      <ResponsiveTable striped>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8125rem", py: 1.5 }}>
              {t("regulatoryFeatures.position")}
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8125rem", py: 1.5 }}>
              {t("regulatoryFeatures.characteristic")}
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8125rem", py: 1.5 }}>
              {t("regulatoryFeatures.description")}
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8125rem", py: 1.5 }}>
              {t("regulatoryFeatures.main")}
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8125rem", py: 1.5 }}>
              {t("regulatoryFeatures.prescription")}
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8125rem", py: 1.5 }}>
              {t("regulatoryFeatures.vbnVersionDate")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {regulatoryFeatures?.map((row, index) => (
            <TableRow key={index}>
              <TableCell sx={{ fontSize: "0.8125rem", py: 1.5 }}>
                <strong>{row.presentationOrder}</strong>
              </TableCell>
              <TableCell sx={{ fontSize: "0.8125rem", py: 1.5 }}>{row.characteristicCode}</TableCell>
              <TableCell sx={{ fontSize: "0.8125rem", py: 1.5 }}>{row.characteristic?.name}</TableCell>
              <TableCell sx={{ py: 0, px: 2 }}>
                <Checkbox
                  checked={row.isPrimaryCharacteristic ?? false}
                  onChange={() =>
                    canSetPrimary &&
                    row.characteristicCode &&
                    handleCheckboxChange(row.characteristicCode)
                  }
                  disabled={
                    isPending || !productDetails?.code || !canSetPrimary
                  }
                  size="small"
                  sx={{ p: 0 }}
                />
              </TableCell>
              <TableCell sx={{ fontSize: "0.8125rem", py: 1.5 }}>{row.regulationTypeCode}</TableCell>
              <TableCell sx={{ fontSize: "0.8125rem", py: 1.5 }}>{formatDateTime(row.vbnChangeDateTime)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </ResponsiveTable>
    </TableContainer>
  );
}
